// EnterEmailComponent.js
import React, { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  emailValidAtom,
  emailCheckWelcomeNameAtom,
  registeredUserIdAtom,
  messageForLoginModalAtom,
  onboardingScreenAtom,
  onboardingCurrentScreenAtom,
  onboardingCompletedAtom,
} from "@/store/atoms";
import LoginWithButton from "@/components/common/LoginWithButton";

import {
  useEmailCheck,
  useI18N,
  useRegisterSocialDesktop,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useGoogleLogin } from "@react-oauth/google";
import ReactFacebookLogin from "react-facebook-login";
import FacebookCircleIcon from "@/components/Buttons/FacebookCircleIcon";
import AppleLogin from "react-apple-login";
import {
  appleLoginClientId,
  facebookLoginClientId,
  appleLoginDesktopRedirectUri,
} from "common/config";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FadingMessage from "@/components/common/FadingMessage";

import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

const SafeFacebookLogin = ReactFacebookLogin as any;

interface EnterEmailProps {
  onClose: () => void;
  title: string;
}

const EnterEmailComponent = ({ onClose, title }: EnterEmailProps) => {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();

  const showCustomToast = useCustomToast();

  const [email, setEmail] = useAtom(emailAtom);
  const [, setEmailValid] = useAtom(emailValidAtom);
  const [, setEmailCheckWelcomeName] = useAtom(emailCheckWelcomeNameAtom);
  const [, setRegisteredUserId] = useAtom(registeredUserIdAtom);
  const [mesageForLoginModal, setMesageForLoginModal] = useAtom(
    messageForLoginModalAtom,
  );
  const [, setVisible] = useState(true); // State for visibility

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { setScreenIndex } = useAuthModals();

  const { checkEmail, isLoading, error, result } = useEmailCheck();

  const appleScopeParams = "name email";
  const appleResponseType = "code id_token";
  const appleResponseMode = "form_post";

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const [, setIsOnboardingCompleted] = useAtom(onboardingCompletedAtom);

  const handleOpenOnboardingSliderModal = () => {
    console.log("in EPC handleOpenOnboardingSliderModal");
    setOnboardingCurrentScreen(0);
    setIsOnboardingModalOpen(true);
    setIsOnboardingCompleted(false);
  };

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  const {
    registerSocialDesktop,
    error: errorSocial,
    isLoading: isLoadingSocial,
    data,
  } = useRegisterSocialDesktop();

  useEffect(() => {
    AnalyticsService.trackGAPagesHandler("authPopupOpen");
    AnalyticsService.trackMx("authPopupOpen", {});
  }, []);

  useEffect(() => {
    if (data) {
      console.log("Login done. data=", data);
      console.log("Login done. data.firstLogin=", data?.data?.firstLogin);
      const socialLoginStatus = data?.data?.social_login_status;
      const userId = data?.data?.id;
      if (socialLoginStatus == "register") {
        AnalyticsService.trackGAPagesHandler(
          "authPopupSocialLoginCreatedNewAccount",
        );
        AnalyticsService.trackMx("authPopupSocialLoginCreatedNewAccount", {});

        setRegisteredUserId(userId);
        const userDataCompat = {
          id: userId,
          ability: "",
          accessibility_need: "",
          email,
          gender: "",
          name: "",
          year_of_birth: "",
          level: 1,
        };

        AnalyticsService.trackGAPagesHandler(
          "authPopupSocialLoginLoggedInMixpanelSuperPropertySet",
        );
        AnalyticsService.trackMx(
          "authPopupSocialLoginLoggedInMixpanelSuperPropertySet",
          {
            EmailAddress: email,
          },
        );

        AnalyticsService.setUserProfileProps(
          userDataCompat,
          {
            EmailConfirmed: false,
            HowDoYouHearAboutSociability: "",
          },
          {
            EmailConfirmed: false,
          },
        );

        // show onBording
        setScreenIndex(AuthModalScreen.OnboardingStep1);
      } else {
        AnalyticsService.trackGAPagesHandler("authPopupSocialLoginLoggedIn");
        AnalyticsService.trackMx("authPopupSocialLoginLoggedIn", {});

        if (data?.data?.firstLogin == 1) {
          AnalyticsService.trackGAPagesHandler(
            "authPopupFirstLoginOnboardingOpen",
          );
          AnalyticsService.trackMx("authPopupFirstLoginOnboardingOpen", {
            EmailAddress: email,
          });

          //call open onboarding slider
          handleOpenOnboardingSliderModal();
        }

        setScreenIndex(AuthModalScreen.EnterEmail);
        onClose();
      }
    }
  }, [data, email, onClose, setRegisteredUserId, setScreenIndex]);

  const handleClose = () => {
    AnalyticsService.trackGAPagesHandler("authPopupClose");
    AnalyticsService.trackMx("authPopupClose", {});
    onClose();
  };

  useEffect(() => {
    if (errorSocial) {
      AnalyticsService.trackGAPagesHandler(
        "authPopupSocialLoginErrorSigningIn",
      );
      AnalyticsService.trackMx("authPopupSocialLoginErrorSigningIn", {});

      console.log("errorSocial", errorSocial);
      // setMesageForLoginModal(errorSocial?.message);
      showCustomToast(errorSocial?.message, "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSocial]);

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      AnalyticsService.trackGAEventHandler("authPopupSocialLogin");
      AnalyticsService.trackMx("authPopupSocialLogin", {
        SocialPlatform: "google",
      });

      // You can now use the tokenResponse to authenticate the user in your app
      const access_token = tokenResponse.code;
      // console.log("Google login access_token", access_token);

      registerSocialDesktop({
        provider: "google",
        authToken: access_token,
      });
    },
    onError: () => {
      console.error("Google login failed");
      // Handle login errors here
    },
    flow: "auth-code", // Use'auth-code' for the authorization code flow
  });

  const handleFacebookResponse = (response) => {
    // console.log("Facebook login response:", response);
    // console.log("Facebook login accessToken:", response.accessToken);

    if (response.accessToken) {
      AnalyticsService.trackGAEventHandler("authPopupSocialLogin");
      AnalyticsService.trackMx("authPopupSocialLogin", {
        SocialPlatform: "facebook",
      });

      // console.log("Facebook login send backend request:");

      registerSocialDesktop({
        provider: "facebook",
        authToken: response.accessToken,
      });
    } else {
      //show alert message login failed
    }
  };

  const handleAppleResponse = (response) => {
    console.log("Apple login response:", response);

    AnalyticsService.trackGAEventHandler("authPopupSocialLogin");
    AnalyticsService.trackMx("authPopupSocialLogin", {
      SocialPlatform: "apple",
    });

    registerSocialDesktop({
      provider: "apple",
      authToken: response.authorization.id_token,
    });
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailValid(validateEmail(e.target.value));
  };

  const handleCheckEmailContinue = (email: string) => {
    if (validateEmail(email)) {
      AnalyticsService.trackGAEventHandler("authPopupEmailSubmit");
      AnalyticsService.trackMx("authPopupEmailSubmit", {
        EmailAddress: email,
      });

      // console.log("email is valid send API call");
      checkEmail(email);
    } else {
      setErrorMessage("Invalid email address");
    }
  };

  useEffect(() => {
    if (result) {
      if (result.social_status == "success") {
        setErrorMessage(result?.message);
      } else if (result.status === "error") {
        AnalyticsService.trackGAEventHandler("authPopupEmailNotExist");
        AnalyticsService.trackMx("authPopupEmailNotExist", {
          EmailAddress: email,
        });
        console.log("Status Error");

        AnalyticsService.trackGAEventHandler("authPopupEnterPasswordOpen");
        AnalyticsService.trackMx("authPopupEnterPasswordOpen", {});

        setScreenIndex(AuthModalScreen.Register);
        setErrorMessage(result?.message);
      } else {
        AnalyticsService.trackGAEventHandler("authPopupEmailExist");
        AnalyticsService.trackMx("authPopupEmailExist", {
          EmailAddress: email,
        });
        setEmailCheckWelcomeName(result?.data?.first_name);
        setScreenIndex(AuthModalScreen.EnterPassword);
      }
    }
  }, [result, setScreenIndex, setEmailCheckWelcomeName]);

  useEffect(() => {
    if (error !== null) {
      console.log("error", error);
      AnalyticsService.trackGAEventHandler("authPopupEmailValidationError");
      AnalyticsService.trackMx("authPopupEmailValidationError", {});

      setEmailCheckWelcomeName("");
      console.log("500 Error");
      setErrorMessage(error?.message);
    }
  }, [error]);

  useEffect(() => {
    if (mesageForLoginModal !== "") {
      setVisible(true); // Set visibility to true when message is set
      setTimeout(() => {
        setVisible(false); // Set visibility to false to start fade out
      }, 2000); // Adjust the duration as needed
      setTimeout(() => {
        setMesageForLoginModal(""); // Clear the message after fade out
      }, 5000); // Adjust the duration to match the fade out time
    }
  }, [mesageForLoginModal, setMesageForLoginModal]);

  return (
    <div
      style={modalStyle}
      className={cn(modalClassName)}
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={handleClose}
          variant="closeModal"
          icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} aria-hidden="true" />}
          aria-label="Close modal"
          tabIndex={0}
          className="flex-shrink-0"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          ref={firstFocusRef}
          tabIndex={-1}
          id="modal-title"
          onBlur={(e) => {
            // Remove tabindex after focus to prevent it from being in tab order
            e.currentTarget.removeAttribute("tabindex");
          }}
        >
          {title}
        </h1>
        <div className="flex-shrink-0 w-10"></div>{" "}
        {/* Spacer to balance the layout */}
      </header>

      <div
        className="py-3 px-6 md:py-6 md:px-14"
      >
        <div className="w-full mx-auto">
          {mesageForLoginModal !== "" && (
            <FadingMessage message={mesageForLoginModal} delay={5000} />
          )}

          <form
            className="flex flex-col gap-6 pb-8 mt-6"
            onSubmit={(e) => {
              e.preventDefault();
              handleCheckEmailContinue(email);
            }}
          >
            <FloatingLabelInput
              required
              label="Email"
              type="email"
              value={email}
              placeholder="Email"
              onChange={handleEmailChange}
              onClear={() => {
                setEmailValid(false);
                setErrorMessage(null);
                setEmail("");
              }}
              errorMessage={errorMessage}
            />

            <div className="flex justify-center w-full">
              <WideButton
                disabled={email.length === 0}
                buttonType="submit"
              >
                {i18n.get("continue")}
              </WideButton>
            </div>

            {isLoading && <Loader />}

            {isLoadingSocial && <Loader />}
          </form>

          <div className="flex flex-col gap-6 pb-8">
            <div>
              <div className="flex items-center justify-center gap-4">
                <hr className="w-full border-border-token-brand-minimal" />
                <span className="title-s-semibold text-fg-neutral-strong">
                  or
                </span>
                <hr className="w-full border-border-token-brand-minimal" />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <AppleLogin
                clientId={appleLoginClientId}
                redirectURI={appleLoginDesktopRedirectUri}
                callback={handleAppleResponse} // Catch the response
                scope={appleScopeParams}
                responseType={appleResponseType}
                responseMode={appleResponseMode}
                usePopup={true}
                render={(
                  renderProps, //Custom Apple Sign in Button
                ) => (
                  <LoginWithButton
                    iconSrc={
                      isDark
                        ? "/images/AppleIcon_dark.svg"
                        : "/images/AppleIcon.svg"
                    }
                    onClick={renderProps.onClick}
                    aria-label="Continue with Apple"
                  >
                    {i18n.get("continueWithApple")}
                  </LoginWithButton>
                )}
              />

              <LoginWithButton
                iconSrc="/images/GoogleIcon.svg"
                onClick={() => googleLogin()}
                aria-label="Continue with Google"
              >
                {i18n.get("continueWithGoogle")}
              </LoginWithButton>

              <SafeFacebookLogin
                appId={facebookLoginClientId}
                autoLoad={false}
                fields="name,email,picture"
                callback={handleFacebookResponse}
                cssClass={`w-full flex ${!isZoomedIn ? "flex-row items-center justify-center text-center" : "flex-col items-center justify-center text-center"} gap-4 px-4 py-3 bg-container-brand-minimal rounded-xl border border-border-token-brand-minimal text-fg-neutral-strong title-s-semibold whitespace-normal break-words`}
                icon={<FacebookCircleIcon aria-hidden="true" />}
                textButton={i18n.get("continueWithFacebook")}
                aria-label="Continue with Facebook"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterEmailComponent;
